import type { BoxProps } from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import Box from '@material-ui/core/Box';

import { FlowType } from '@jerry-serverless/types';

import type { SelectedServiceType } from '../../types/app_data';
import { StarRating } from '../star_rating/StarRating';

import PhotoImage from './assets/photo.webp';
import Photo3Image from './assets/photo3.webp';

interface TruesReviewCopy {
  name: string;
  saved?: string;
  savedUnit?: string;
  comment: string;
  reviewed?: string;
  hidePhoto?: boolean;
  photo?: string;
}

interface TrustReviewCardProps extends BoxProps {
  route: string;
  selectedService?: SelectedServiceType;
  flowType?: FlowType;
  hideStars?: boolean;
}

/**
 * Steps that need TrustReviewCard in GarageOnly flow.
 */
export const showTrustReviewCardPathsOfGarageOnlyFlow = [
  'garageonlystart',
  'garageonlyreminder',
  'MaintenanceOilChange',
  'MaintenanceOilType',
  'MaintenanceTireRotated',
  'MaintenanceBrakePad',
  'garageonlyaddcarmileagereminder',
];

const garageOnlyTrustSteps1 = [
  'garageonlystart',
  'addCarYear',
  'addCarMake',
  'addCarModel',
  'addCarSubmodel',
  'garageonlycarallset',
  'garageonlyestimateservice',
  'garageonlyservicedetails',
  'garageonlyserviceallset',
];

const garageOnlyTrustSteps2 = [
  'garageonlyreminder',
  'MaintenanceOilChange',
  'MaintenanceOilType',
  'MaintenanceTireRotated',
  'MaintenanceBrakePad',
  'garageonlyaddcarmileagereminder',
];

/**
 * Get trust review copies.
 */
export const useGetTrustReviewCopy = (
  stepName: string,
  selectedService?: SelectedServiceType,
  flowType?: FlowType
): TruesReviewCopy => {
  return useMemo(() => {
    if (selectedService?.refi) {
      return {
        name: 'JJ S.',
        saved: '$214',
        savedUnit: 'a month',
        comment: 'The process was terrific — I had no trouble uploading my info. Thank you so much.',
      };
    }
    if (selectedService?.garage && flowType && [FlowType.RepairEstimate].includes(flowType)) {
      if (garageOnlyTrustSteps1.includes(stepName)) {
        return {
          name: 'Jean K.',
          comment:
            'Being able to walk in knowing how much I should be paying for repairs gave me the confidence to get my car fixed!',
          reviewed: '12/10/2024',
          hidePhoto: true,
        };
      }
      if (garageOnlyTrustSteps2.includes(stepName)) {
        return {
          name: 'Cammy R.',
          reviewed: '07/12/2023',
          comment: `Jerry does very good job at actually keeping you updated on your vehicle's maintenance.`,
          hidePhoto: true,
        };
      }
      return {
        name: 'Che',
        reviewed: '04/20/2023',
        hidePhoto: true,
        comment:
          'Jerry is actually a really good app!! It can do everything from tell you if there are recalls on your vehicle to actually helping you schedule maintenance for said vehicle!',
      };
    }
    if (
      selectedService?.garage &&
      flowType &&
      [FlowType.CarValue, FlowType.CarRecall, FlowType.Maintenance, FlowType.RepairShop].includes(flowType)
    ) {
      return {
        name: 'Jeanette M.',
        comment:
          'This app made the process so easy and painless. Have already recommended the app to two people and plan on sharing it to many others.',
        hidePhoto: false,
        photo: Photo3Image,
      };
    }
    return {
      name: 'Morgan F',
      saved: '$370',
      comment:
        'In just a minute, I had many quotes saving me hundreds of dollars a year. So, I switched. Better coverage, lower price, and questions still just a phone call or text away. You can’t afford not to try.',
    };
  }, [selectedService, flowType, stepName]);
};

/**
 * Trust review card
 */
export const TrustReviewCard: FunctionComponent<TrustReviewCardProps> = ({
  route,
  selectedService,
  flowType,
  hideStars = false,
  ...boxProps
}): JSX.Element => {
  const trustReviewCopy = useGetTrustReviewCopy(route, selectedService, flowType);
  return (
    <Box>
      {!hideStars && <StarRating />}
      <Box display="flex" alignItems="center" my={1}>
        {!trustReviewCopy.hidePhoto && (
          <img alt="avatar" src={trustReviewCopy.photo ?? PhotoImage} style={{ width: 24, height: 24 }} />
        )}
        <Box ml={!trustReviewCopy.hidePhoto ? 0.5 : 0} textAlign="left" fontSize={14}>
          <span style={{ fontWeight: 500 }}>{trustReviewCopy.name}</span>
          {trustReviewCopy.reviewed && (
            <span style={{ color: '#6e7e91' }}> reviewed on {trustReviewCopy.reviewed}</span>
          )}
          {trustReviewCopy.saved && (
            <>
              <span style={{ color: '#6e7e91' }}> saved </span>
              <span style={{ fontWeight: 500 }}>
                {trustReviewCopy.saved}
                {trustReviewCopy.savedUnit && (
                  <Typography variant="inherit" className="grayFontColor">
                    {` ${trustReviewCopy.savedUnit} `}
                  </Typography>
                )}
              </span>
            </>
          )}
        </Box>
      </Box>
      <Typography style={{ fontSize: '0.75rem', color: '#6e7e91', lineHeight: '0.93rem' }}>
        {trustReviewCopy.comment}
      </Typography>
    </Box>
  );
};
